import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import FlashcardGame from './FlashcardGame';
import { formatTime, getTodayTime, saveTodayTime, getTotalTime, getTotalTodayTime, getTotalYesterdayTime, getTotalThisWeekTime } from '../utils/timeUtils';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../services/dataService';
import { fetchAllVocabWords } from '../services/wordBankService';
import type { VocabWord } from '../types/vocab';
import { useSubscription } from '../hooks/useSubscription';
import PaywallModal from './PaywallModal';
import { supabase } from '../services/supabaseClient';

type WordData = VocabWord;

interface DaySchedule {
  day: number;
  learningGroup: number;
  rotatingGroups: number[];
  recentGroups: number[];
  allGroups: number[];
}

interface GroupProgress {
  groupNumber: number;
  completed: boolean;
  completedAt?: number;
}

interface FastAttackProgress {
  currentDay: number;
  totalDays: number;
  groupProgress: { [groupNumber: number]: GroupProgress };
  dayCompleted: { [day: number]: boolean };
  sessionState?: {
    day: number;
    cardIndex: number;
    answeredWords: string[];
  };
}

const ATTACK_PRESETS = [
  { id: 'sprint',   label: 'Sprint',   days: 30, bestFor: 'Test in 4–5 weeks'  },
  { id: 'standard', label: 'Standard', days: 38, bestFor: 'Test in 5–6 weeks'  },
  { id: 'steady',   label: 'Steady',   days: 45, bestFor: 'Test in 6–8 weeks'  },
  { id: 'marathon', label: 'Marathon', days: 60, bestFor: 'Low daily pressure · best long-term' },
] as const;

type PresetId = typeof ATTACK_PRESETS[number]['id'];

const LightningIcon = ({ size = 18, color = '#1F4C40' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M13 2L6 14h5l-1 8 7-12h-5z" />
  </svg>
);

const FastAttackGame: React.FC = () => {
  const { user } = useAuth();
  const { isPro } = useSubscription();
  const [showPaywall, setShowPaywall] = useState(false);
  const [words, setWords] = useState<WordData[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalDays, setTotalDays] = useState(38);
  const [currentDay, setCurrentDay] = useState(1);
  const [showSetup, setShowSetup] = useState(true);
  const [progress, setProgress] = useState<FastAttackProgress | null>(null);
  const [activeGroups, setActiveGroups] = useState<number[]>([]);
  const [showStudyMode, setShowStudyMode] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0); // Time in seconds
  const [todayTime, setTodayTime] = useState(0); // Time spent today
  const [totalTime, setTotalTime] = useState(0); // Total time across all modes
  const [totalTodayTime, setTotalTodayTime] = useState(0); // Total time today across all modes
  const [scheduleExpanded, setScheduleExpanded] = useState(false);
  const [showAllScheduleDays, setShowAllScheduleDays] = useState(false);
  const [selectedMode, setSelectedMode] = useState<PresetId | 'custom'>('standard');
  const [customInputFocused, setCustomInputFocused] = useState(false);
  const sessionStartTimeRef = useRef<number | null>(null);
  const todaySessionStartRef = useRef<number | null>(null);
  const [gameSessionActive, setGameSessionActive] = useState(false);
  const startButtonRef = useRef<HTMLButtonElement>(null);
  const initialSavedTimeRef = useRef<number>(0); // Track initial saved time to avoid double counting
  const initialTodayTimeRef = useRef<number>(0); // Track initial saved today time
  const sessionSaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load words from Supabase
  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    fetchAllVocabWords()
      .then(({ data, error }) => {
        if (!isMounted) return;

        if (error) {
          console.error('Error loading vocab from Supabase:', error.message);
          setLoading(false);
          return;
        }

        if (!data || data.length === 0) {
          console.error('No vocab words found in Supabase.');
          setLoading(false);
          return;
        }

        const sortedData = [...data].sort((a, b) => a.Word.localeCompare(b.Word));
        setWords(sortedData);
        setLoading(false);
      })
      .catch(error => {
        if (!isMounted) return;
        console.error('Unexpected error loading vocab from Supabase:', error);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Load progress from localStorage and Supabase
  useEffect(() => {
    let isMounted = true;

    const loadFromLocalStorage = () => {
      try {
        const savedProgress = localStorage.getItem('gre_fast_attack_progress');
        const savedTotalDays = localStorage.getItem('gre_fast_attack_total_days');
        const savedCurrentDay = localStorage.getItem('gre_fast_attack_current_day');

        if (savedProgress) {
          const parsed = JSON.parse(savedProgress);
          if (isMounted) {
            setProgress(parsed);
            setShowSetup(false);
          }
        }

        if (savedTotalDays) {
          setTotalDays(parseInt(savedTotalDays, 10));
        }

        if (savedCurrentDay) {
          setCurrentDay(parseInt(savedCurrentDay, 10));
        }

        const savedTime = localStorage.getItem('gre_fast_attack_time');
        if (savedTime) {
          try {
            const savedTimeSeconds = parseInt(savedTime, 10);
            if (!isNaN(savedTimeSeconds) && savedTimeSeconds >= 0) {
              initialSavedTimeRef.current = savedTimeSeconds;
              setElapsedTime(savedTimeSeconds);
            }
          } catch (e) {
            console.error('Error parsing saved time:', e);
          }
        }

        const todayTimeValue = getTodayTime('fastattack');
        initialTodayTimeRef.current = todayTimeValue;
        setTodayTime(todayTimeValue);
        setTotalTime(getTotalTime());
        setTotalTodayTime(getTotalTodayTime());
        sessionStartTimeRef.current = Date.now();
        todaySessionStartRef.current = Date.now();
      } catch (e) {
        console.error('Error loading progress:', e);
      }
    };

    const loadFromSupabase = async () => {
      if (!user) return;
      try {
        const { data, error } = await dataService.fastAttackProgress.get(user.id);
        if (!isMounted || error || !data) return;

        if (data.progress) {
          const supaProgress: FastAttackProgress = {
            ...data.progress,
            totalDays: data.progress.totalDays || data.total_days || totalDays,
            currentDay: data.progress.currentDay || data.current_day || 1
          };
          setProgress(supaProgress);
          setShowSetup(false);
          setTotalDays(supaProgress.totalDays);
          setCurrentDay(supaProgress.currentDay);
          try {
            localStorage.setItem('gre_fast_attack_progress', JSON.stringify(supaProgress));
            localStorage.setItem('gre_fast_attack_total_days', supaProgress.totalDays.toString());
            localStorage.setItem('gre_fast_attack_current_day', supaProgress.currentDay.toString());
          } catch (e) {
            console.error('Error caching fast attack progress locally:', e);
          }
        } else {
          setShowSetup(true);
          setProgress(null);
          try {
            if (data.total_days) {
              setTotalDays(data.total_days);
              localStorage.setItem('gre_fast_attack_total_days', data.total_days.toString());
            }
            if (data.current_day) {
              setCurrentDay(data.current_day);
              localStorage.setItem('gre_fast_attack_current_day', data.current_day.toString());
            }
          } catch (e) {
            console.error('Error caching fast attack metadata locally:', e);
          }
        }
      } catch (e) {
        console.error('Unexpected error loading fast attack progress from Supabase:', e);
      }
    };

    loadFromLocalStorage();
    if (user) {
      loadFromSupabase();
    }

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Timer effect - update every second, only runs after user starts a day
  useEffect(() => {
    if (!gameSessionActive) return;

    if (!sessionStartTimeRef.current) {
      sessionStartTimeRef.current = Date.now();
    }
    if (!todaySessionStartRef.current) {
      todaySessionStartRef.current = Date.now();
    }

    const interval = setInterval(() => {
      if (sessionStartTimeRef.current && todaySessionStartRef.current) {
        const now = Date.now();

        // Mode-specific timer - use initial saved time + current session elapsed
        const sessionElapsed = Math.floor((now - sessionStartTimeRef.current) / 1000);
        const totalElapsed = initialSavedTimeRef.current + sessionElapsed;
        setElapsedTime(totalElapsed);

        // Today's timer - use initial saved today time + current session elapsed
        const todaySessionElapsed = Math.floor((now - todaySessionStartRef.current) / 1000);
        const totalTodayElapsed = initialTodayTimeRef.current + todaySessionElapsed;
        setTodayTime(totalTodayElapsed);

        // Total time across all modes (recalculate from localStorage)
        setTotalTime(getTotalTime());
        setTotalTodayTime(getTotalTodayTime());

        // Save to localStorage every 5 seconds
        if (sessionElapsed % 5 === 0 && sessionElapsed > 0) {
          try {
            localStorage.setItem('gre_fast_attack_time', totalElapsed.toString());
            saveTodayTime('fastattack', totalTodayElapsed);
            // Update initial saved time to the new total, then reset session start
            initialSavedTimeRef.current = totalElapsed;
            initialTodayTimeRef.current = totalTodayElapsed;
            sessionStartTimeRef.current = Date.now();
            todaySessionStartRef.current = Date.now();
          } catch (e) {
            console.error('Error saving time to localStorage:', e);
          }
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [gameSessionActive]);

  // Reset idle screen when the current day changes
  useEffect(() => {
    setGameSessionActive(false);
  }, [currentDay]);

  // Split words into groups of 30 by interleaving so each group has
  // variety across the full vocabulary instead of one letter of the alphabet.
  // Word i goes to group (i % numGroups), giving every group a word from
  // every ~numGroups-th position in the sorted list.
  const groups = useMemo(() => {
    if (words.length === 0) return [];

    const numGroups = Math.ceil(words.length / 30);
    const groupsList: WordData[][] = Array.from({ length: numGroups }, () => []);
    words.forEach((word, i) => {
      groupsList[i % numGroups].push(word);
    });
    return groupsList;
  }, [words]);

  const totalGroups = groups.length;

  // Calculate schedule for each day
  const calculateSchedule = (day: number, totalGroupsParam: number, totalDaysParam: number): DaySchedule => {
    // Calculate which groups to learn on this day
    // Distribute all groups across totalDays
    const groupsPerDay = totalGroupsParam / totalDaysParam;
    const startGroup = Math.floor((day - 1) * groupsPerDay) + 1;
    const endGroup = Math.min(totalGroupsParam, Math.ceil(day * groupsPerDay));

    const learningGroups: number[] = [];
    for (let g = startGroup; g <= endGroup; g++) {
      learningGroups.push(g);
    }

    // Primary learning group is the first one (or the day number if we're using one per day)
    const learningGroup = learningGroups.length > 0 ? learningGroups[0] : Math.min(day, totalGroupsParam);

    // Rotating set of 5 groups - adapt based on total days
    let rotatingGroups: number[] = [];
    const reviewStartDay = Math.ceil(totalDaysParam * 0.2); // Start review after 20% of days
    const blockSize = 5;

    if (day >= reviewStartDay) {
      // Calculate how many blocks we can have
      const numBlocks = Math.ceil(totalGroupsParam / blockSize);

      // Days reviewStartDay to reviewStartDay+4: Review first block
      const firstBlockEndDay = reviewStartDay + blockSize - 1;
      if (day <= firstBlockEndDay) {
        // Review first block
        const firstBlockEnd = Math.min(blockSize, totalGroupsParam);
        for (let g = 1; g <= firstBlockEnd; g++) {
          rotatingGroups.push(g);
        }
      } else {
        // Alternate between blocks
        const dayOffset = day - (firstBlockEndDay + 1);
        const blockIndex = Math.floor(dayOffset / blockSize) % numBlocks;
        const blockStart = blockIndex * blockSize + 1;
        const blockEnd = Math.min((blockIndex + 1) * blockSize, totalGroupsParam);

        for (let g = blockStart; g <= blockEnd; g++) {
          rotatingGroups.push(g);
        }
      }
    }

    // Recent groups (up to 4 most recent groups not in rotating set)
    const recentGroups: number[] = [];
    if (day > 1) {
      // Find groups learned in recent days (up to 4 days back)
      const recentDaysLookback = 4;
      const groupsInRecentDays: number[] = [];

      for (let d = Math.max(1, day - recentDaysLookback); d < day; d++) {
        const dayGroupsPerDay = totalGroupsParam / totalDaysParam;
        const dayStartGroup = Math.floor((d - 1) * dayGroupsPerDay) + 1;
        const dayEndGroup = Math.min(totalGroupsParam, Math.ceil(d * dayGroupsPerDay));

        for (let g = dayStartGroup; g <= dayEndGroup; g++) {
          if (!groupsInRecentDays.includes(g)) {
            groupsInRecentDays.push(g);
          }
        }
      }

      // Add recent groups that aren't in rotating set or learning groups
      for (const groupNum of groupsInRecentDays.reverse()) {
        if (!rotatingGroups.includes(groupNum) &&
            !learningGroups.includes(groupNum) &&
            groupNum <= totalGroupsParam) {
          recentGroups.push(groupNum);
          if (recentGroups.length >= 4) break;
        }
      }
    }

    // Combine all groups for the day (include all learning groups, not just the first one)
    const allGroups = [...learningGroups, ...rotatingGroups, ...recentGroups]
      .filter(g => g <= totalGroupsParam)
      .filter((g, i, arr) => arr.indexOf(g) === i) // Remove duplicates
      .sort((a, b) => a - b);

    return {
      day,
      learningGroup, // Keep for display, but allGroups includes all learning groups
      rotatingGroups,
      recentGroups,
      allGroups
    };
  };

  // Generate schedule for all days
  const schedule = useMemo(() => {
    if (totalGroups === 0 || totalDays === 0) return [];
    const scheduleList: DaySchedule[] = [];
    for (let day = 1; day <= totalDays; day++) {
      scheduleList.push(calculateSchedule(day, totalGroups, totalDays));
    }
    return scheduleList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalDays, totalGroups]);

  // Get current day schedule
  const currentSchedule = schedule[currentDay - 1] || null;

  // Initialize progress
  const initializeProgress = () => {
    const newProgress: FastAttackProgress = {
      currentDay: 1,
      totalDays: totalDays,
      groupProgress: {},
      dayCompleted: {}
    };

    // Initialize all groups
    for (let i = 1; i <= totalGroups; i++) {
      newProgress.groupProgress[i] = {
        groupNumber: i,
        completed: false
      };
    }

    setProgress(newProgress);
    setCurrentDay(1);
    setShowSetup(false);
    saveProgress(newProgress);
  };

  // Save progress to localStorage and Supabase
  const saveProgress = (progressToSave: FastAttackProgress) => {
    try {
      localStorage.setItem('gre_fast_attack_progress', JSON.stringify(progressToSave));
      localStorage.setItem('gre_fast_attack_total_days', progressToSave.totalDays.toString());
      localStorage.setItem('gre_fast_attack_current_day', progressToSave.currentDay.toString());
    } catch (e) {
      console.error('Error saving progress:', e);
    }

    if (user) {
      dataService.fastAttackProgress
        .upsert({
          user_id: user.id,
          total_days: progressToSave.totalDays,
          current_day: progressToSave.currentDay,
          progress: progressToSave
        })
        .catch(err => console.error('Error saving fast attack progress to Supabase:', err));
    }
  };

  // Called by FlashcardGame on every card navigation / answer — saves session state to Supabase
  const handleCardProgress = useCallback((cardIndex: number, answeredWords: string[]) => {
    if (!progress) return;
    const updated: FastAttackProgress = {
      ...progress,
      sessionState: { day: currentDay, cardIndex, answeredWords },
    };
    // Persist to localStorage immediately
    try {
      localStorage.setItem('gre_fast_attack_progress', JSON.stringify(updated));
    } catch {}
    // Debounce Supabase write — 3 s after last change
    if (sessionSaveDebounceRef.current) clearTimeout(sessionSaveDebounceRef.current);
    sessionSaveDebounceRef.current = setTimeout(() => {
      if (user) {
        dataService.fastAttackProgress
          .upsert({ user_id: user.id, total_days: updated.totalDays, current_day: updated.currentDay, progress: updated })
          .catch(err => console.error('Error saving session progress to Supabase:', err));
      }
    }, 3000);
  }, [progress, currentDay, user]);

  // Start studying current day
  const startDay = () => {
    if (!currentSchedule) return;

    setActiveGroups(currentSchedule.allGroups);
    setShowStudyMode(true);
  };

  // Mark group as completed
  const markGroupCompleted = (groupNumber: number) => {
    if (!progress) return;

    const updatedProgress = {
      ...progress,
      groupProgress: {
        ...progress.groupProgress,
        [groupNumber]: {
          groupNumber,
          completed: true,
          completedAt: Date.now()
        }
      }
    };

    // Check if all groups for current day are completed
    if (currentSchedule) {
      const allCompleted = currentSchedule.allGroups.every(
        g => updatedProgress.groupProgress[g]?.completed
      );

      if (allCompleted) {
        updatedProgress.dayCompleted[currentDay] = true;
        if (user) {
          supabase.rpc('record_session', {
            p_user_id: user.id,
            p_xp_earned: 25,
          });
        }
      }
    }

    setProgress(updatedProgress);
    saveProgress(updatedProgress);
  };

  // Move to next day
  const nextDay = () => {
    if (currentDay < schedule.length) {
      // Clear saved session progress for the day we're leaving
      const prevKey = `fa_day_${currentDay}_${totalDays}`;
      localStorage.removeItem(`gre_fa_${prevKey}_index`);
      localStorage.removeItem(`gre_fa_${prevKey}_answered`);

      const newDay = currentDay + 1;
      setCurrentDay(newDay);
      if (progress) {
        const updatedProgress = { ...progress, currentDay: newDay };
        setProgress(updatedProgress);
        saveProgress(updatedProgress);
      }
      setShowStudyMode(false);
    }
  };

  // Skip to next day
  const skipToNextDay = () => {
    if (currentDay < schedule.length) {
      nextDay();
    }
  };

  // Reset progress
  const resetProgress = async () => {
    if (!window.confirm('Are you sure you want to reset all progress? This cannot be undone.')) {
      return;
    }

    localStorage.removeItem('gre_fast_attack_progress');
    localStorage.removeItem('gre_fast_attack_total_days');
    localStorage.removeItem('gre_fast_attack_current_day');

    // Clear all saved fast attack session progress keys
    Object.keys(localStorage)
      .filter(k => k.startsWith('gre_fa_fa_day_'))
      .forEach(k => localStorage.removeItem(k));

    if (user) {
      try {
        await dataService.fastAttackProgress.delete(user.id);
      } catch (e) {
        console.error('Error clearing fast attack progress from Supabase:', e);
      }
    }

    setProgress(null);
    setCurrentDay(1);
    setShowSetup(true);
    setShowStudyMode(false);
  };

  // Get words for active groups
  const activeWords = useMemo(() => {
    if (activeGroups.length === 0 || groups.length === 0) return [];

    const wordsList: WordData[] = [];
    activeGroups.forEach(groupNum => {
      if (groups[groupNum - 1]) {
        wordsList.push(...groups[groupNum - 1]);
      }
    });

    return wordsList;
  }, [activeGroups, groups]);

  // Check if current day is completed
  const isDayCompleted = currentSchedule
    ? currentSchedule.allGroups.every(g => progress?.groupProgress[g]?.completed)
    : false;

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading words...</div>
      </div>
    );
  }

  if (words.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ color: 'var(--color-danger)', fontSize: '18px', marginBottom: '10px' }}>
          No words loaded.
        </div>
      </div>
    );
  }

  // Show study mode (flashcard game with filtered words)
  if (showStudyMode && activeWords.length > 0) {
    const faProgressKey = `fa_day_${currentDay}_${totalDays}`;
    const clearDayProgress = () => {
      localStorage.removeItem(`gre_fa_${faProgressKey}_index`);
      localStorage.removeItem(`gre_fa_${faProgressKey}_answered`);
      if (sessionSaveDebounceRef.current) clearTimeout(sessionSaveDebounceRef.current);
      if (progress) {
        const { sessionState: _, ...rest } = progress;
        saveProgress(rest as FastAttackProgress);
      }
    };

    return (
      <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
        {/* Slim header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-primary)' }}>
            Day {currentDay} · Fast Attack
          </div>
          <button
            onClick={() => setShowStudyMode(false)}
            style={{
              padding: '5px 14px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', backgroundColor: 'transparent',
              color: 'var(--color-text-muted)',
              border: '1.5px solid var(--color-border)', borderRadius: 999,
            }}
          >
            ← Back
          </button>
        </div>

        {/* Render FlashcardGame with filtered words */}
        {(() => {
          const saved = progress?.sessionState?.day === currentDay ? progress.sessionState : null;
          return (
            <FlashcardGame
              words={activeWords}
              isFastAttack={true}
              progressKey={faProgressKey}
              initialCardIndex={saved?.cardIndex}
              initialAnsweredWords={saved?.answeredWords}
              onCardProgress={handleCardProgress}
              sessionLabel={`Day ${currentDay} · ${currentSchedule?.allGroups.length ?? 0} group${(currentSchedule?.allGroups.length ?? 0) !== 1 ? 's' : ''}`}
              onComplete={() => {
                clearDayProgress();
                if (currentSchedule) {
                  currentSchedule.allGroups.forEach(groupNum => markGroupCompleted(groupNum));
                }
                setShowStudyMode(false);
              }}
            />
          );
        })()}

        {/* Manual fallback — shown only until auto-complete fires */}
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button
            onClick={() => {
              clearDayProgress();
              if (currentSchedule) {
                currentSchedule.allGroups.forEach(groupNum => markGroupCompleted(groupNum));
                setShowStudyMode(false);
              }
            }}
            style={{
              padding: '10px 24px', fontSize: '13px', fontWeight: 400,
              cursor: 'pointer', backgroundColor: 'transparent',
              color: 'var(--color-text-muted)',
              border: '1px solid var(--color-border)', borderRadius: '999px',
            }}
          >
            Mark day complete manually
          </button>
        </div>
      </div>
    );
  }

  // ─── Setup screen ───────────────────────────────────────────────────────────
  if (showSetup) {
    const timeRows = [
      { label: 'Fast attack', value: formatTime(elapsedTime) },
      { label: 'FA today', value: formatTime(todayTime) },
      { label: 'Total all modes', value: formatTime(totalTime) },
      { label: 'Total today', value: formatTime(totalTodayTime) },
      { label: 'Yesterday', value: formatTime(getTotalYesterdayTime()) },
      { label: 'This week', value: formatTime(getTotalThisWeekTime()) },
    ];

    return (
      <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
        <Helmet>
          <title>GRE Fast Attack Mode – Spaced Repetition Vocab | Phrenos</title>
          <meta name="description" content="Daily GRE vocab sessions with built-in spaced repetition. New words every day plus rotating review groups — study 1,100+ words in 30 days." />
          <meta property="og:title" content="GRE Fast Attack Mode – Spaced Repetition Vocab | Phrenos" />
          <meta property="og:description" content="Daily GRE vocab sessions with built-in spaced repetition. New words every day plus rotating review groups — study 1,100+ words in 30 days." />
          <meta property="og:url" content="https://phrenos.app/fast-attack" />
          <link rel="canonical" href="https://phrenos.app/fast-attack" />
        </Helmet>

        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <LightningIcon size={20} color="#1F4C40" />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1F4C40', letterSpacing: '-0.5px' }}>
            Fast attack
          </h1>
        </div>

        {/* How it works card */}
        <div style={{
          backgroundColor: '#F7F7F7',
          borderRadius: 20,
          border: '1px solid #E0E0E0',
          padding: '16px 20px',
          marginBottom: 14,
        }}>
          <p className="eyebrow" style={{ margin: '0 0 12px' }}>How it works</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#478475', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#707070' }}>1 new group per day</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#C0C0C0', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#707070' }}>5 rotating review groups</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#D4B84A', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#707070' }}>up to 4 recent groups</span>
            </div>
          </div>
          <p style={{ fontSize: 11, color: '#9aada8', margin: '12px 0 0' }}>
            Progress auto-saves · {totalGroups} groups total
          </p>
        </div>

        {/* Preset selector */}
        <p className="eyebrow" style={{ margin: '0 0 8px' }}>Choose your pace</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ATTACK_PRESETS.map(preset => {
            const isSelected = selectedMode === preset.id;
            const wordsPerDay = totalGroups > 0 ? Math.round((totalGroups * 30) / preset.days) : 0;
            const finishDate = new Date(Date.now() + preset.days * 86400000)
              .toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return (
              <div
                key={preset.id}
                onClick={() => {
                  setSelectedMode(preset.id);
                  setTotalDays(preset.days);
                  setTimeout(() => startButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  borderRadius: 16,
                  border: isSelected ? '1.5px solid #1F4C40' : '1px solid #E0E0E0',
                  backgroundColor: isSelected ? '#F0F9F5' : '#F7F7F7',
                  cursor: 'pointer',
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#1F4C40', lineHeight: 1.2 }}>{preset.label}</div>
                  <div style={{ fontSize: 11, color: '#707070', marginTop: 2 }}>{preset.bestFor}</div>
                  <div style={{ fontSize: 11, color: '#9aada8', marginTop: 1 }}>{preset.days} days · done by {finishDate}</div>
                </div>
                <span style={{
                  backgroundColor: '#D4EDE6',
                  color: '#1F4C40',
                  borderRadius: 999,
                  padding: '3px 10px',
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                  marginLeft: 12,
                }}>
                  ~{wordsPerDay} new words/day
                </span>
              </div>
            );
          })}

          {/* Custom option */}
          <div
            onClick={() => setSelectedMode('custom')}
            style={{
              padding: '12px 16px',
              borderRadius: 16,
              border: selectedMode === 'custom' ? '1.5px solid #1F4C40' : '1px solid #E0E0E0',
              backgroundColor: selectedMode === 'custom' ? '#F0F9F5' : '#F7F7F7',
              cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: selectedMode === 'custom' ? '#1F4C40' : '#707070', lineHeight: 1.2 }}>Custom</div>
                <div style={{ fontSize: 11, color: '#9aada8', marginTop: 2 }}>Set your own day count</div>
              </div>
            </div>
            {selectedMode === 'custom' && (
              <div style={{ marginTop: 12 }}>
                <input
                  type="number"
                  min="1"
                  value={totalDays}
                  onClick={e => e.stopPropagation()}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value) && value > 0) setTotalDays(value);
                    else if (e.target.value === '') setTotalDays(38);
                  }}
                  onFocus={() => setCustomInputFocused(true)}
                  onBlur={() => setCustomInputFocused(false)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: 22,
                    fontWeight: 800,
                    color: '#1F4C40',
                    backgroundColor: '#fff',
                    border: `1.5px solid ${customInputFocused ? '#1F4C40' : '#E0E0E0'}`,
                    borderRadius: 12,
                    outline: 'none',
                    boxSizing: 'border-box',
                    fontFamily: 'var(--font-sans)',
                    transition: 'border-color 120ms ease',
                  }}
                />
                {totalDays < totalGroups && (
                  <p style={{ fontSize: 11, color: '#C0392B', margin: '6px 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span>⚠</span> Some days will include multiple new groups
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Helper line */}
        {(() => {
          const wordsPerDay = totalGroups > 0 && totalDays > 0 ? Math.round((totalGroups * 30) / totalDays) : 0;
          const finishDate = new Date(Date.now() + totalDays * 86400000)
            .toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          return (
            <p style={{ fontSize: 12, color: '#707070', margin: '8px 0 0' }}>
              {totalGroups} groups · ~{wordsPerDay} words/day · done by {finishDate}
            </p>
          );
        })()}

        {/* Start button */}
        <button
          ref={startButtonRef}
          onClick={initializeProgress}
          style={{
            width: '100%',
            marginTop: 16,
            marginBottom: 14,
            padding: '15px',
            fontSize: 15,
            fontWeight: 700,
            backgroundColor: '#1F4C40',
            color: '#fff',
            border: 'none',
            borderRadius: 999,
            cursor: 'pointer',
          }}
        >
          Start studying
        </button>

        {/* Study time card */}
        <div style={{
          backgroundColor: '#F7F7F7',
          borderRadius: 20,
          border: '1px solid #E0E0E0',
          padding: '16px 20px',
        }}>
          <p className="eyebrow" style={{ margin: '0 0 12px' }}>Your study time</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {timeRows.map(item => (
              <div key={item.label} style={{
                backgroundColor: '#fff',
                border: '1px solid #E0E0E0',
                borderRadius: 12,
                padding: '10px 12px',
              }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#1F4C40', lineHeight: 1, marginBottom: 4 }}>
                  {item.value}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#707070' }}>
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── Main view — schedule and progress ──────────────────────────────────────

  const groupsDone = progress ? Object.values(progress.groupProgress).filter(g => g.completed).length : 0;
  const daysDone = progress ? Object.values(progress.dayCompleted).filter(d => d).length : 0;
  const totalScheduleDays = schedule.length;

  // Circular progress ring math
  const ringRadius = 32;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringProgress = totalScheduleDays > 0 ? currentDay / totalScheduleDays : 0;
  const ringOffset = ringCircumference * (1 - ringProgress);

  // Current day group categories
  const newGroups = currentSchedule
    ? currentSchedule.allGroups.filter(g => !currentSchedule.rotatingGroups.includes(g) && !currentSchedule.recentGroups.includes(g))
    : [];
  const reviewGroups = currentSchedule?.rotatingGroups ?? [];
  const recentGroupsList = currentSchedule?.recentGroups ?? [];

  const todayWordCount = currentSchedule
    ? currentSchedule.allGroups.reduce((sum, g) => sum + (groups[g - 1]?.length || 0), 0)
    : 0;

  const visibleScheduleDays = showAllScheduleDays ? schedule : schedule.slice(0, 5);

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LightningIcon size={20} color="#1F4C40" />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1F4C40', letterSpacing: '-0.5px' }}>
            Fast attack
          </h1>
        </div>
        <button
          onClick={resetProgress}
          style={{
            padding: '5px 12px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            backgroundColor: '#FDECEA',
            color: '#C0392B',
            border: 'none',
            borderRadius: 999,
          }}
        >
          Reset progress
        </button>
      </div>

      {/* Idle start card — shown before the session begins */}
      {!gameSessionActive && (
        <div style={{
          backgroundColor: '#1F4C40',
          borderRadius: '20px',
          padding: '32px 24px',
          textAlign: 'center',
          color: '#FFFFFF',
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚡</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
            Day {currentDay} of {totalDays}
          </div>
          <div style={{ fontSize: 13, color: '#9FE1CB', marginBottom: 24 }}>
            {currentSchedule?.allGroups.length ?? 0} groups · {currentSchedule?.allGroups.reduce((sum, g) => sum + (groups[g - 1]?.length || 0), 0) ?? 0} words today
          </div>
          {showPaywall && <PaywallModal feature="Fast Attack (full play + spaced repetition)" onClose={() => setShowPaywall(false)} />}
          <button
            onClick={() => { if (!isPro) { setShowPaywall(true); } else { setGameSessionActive(true); } }}
            style={{
              backgroundColor: '#FFFFFF',
              color: '#1F4C40',
              border: 'none',
              borderRadius: 999,
              padding: '14px 40px',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              width: '100%',
            }}
          >
            {isPro ? `Start Day ${currentDay}` : '🔒 Upgrade to play'}
          </button>
        </div>
      )}

      {/* Progress card */}
      <div style={{
        backgroundColor: '#F7F7F7',
        borderRadius: 20,
        border: '1px solid #E0E0E0',
        padding: '20px',
        marginBottom: 14,
      }}>
        {/* Ring + stats row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 16 }}>
          {/* Circular progress ring */}
          <div style={{ flexShrink: 0 }}>
            <svg width={80} height={80} viewBox="0 0 80 80">
              {/* Track */}
              <circle cx={40} cy={40} r={ringRadius} fill="none" stroke="#E0E0E0" strokeWidth={7} />
              {/* Progress arc */}
              <circle
                cx={40} cy={40} r={ringRadius}
                fill="none"
                stroke="#1F4C40"
                strokeWidth={7}
                strokeDasharray={ringCircumference}
                strokeDashoffset={ringOffset}
                strokeLinecap="round"
                style={{ transform: 'rotate(-90deg)', transformOrigin: '40px 40px' }}
              />
              {/* Day label */}
              <text x={40} y={36} textAnchor="middle" fontSize={18} fontWeight={800} fill="#1F4C40" fontFamily="inherit">
                {currentDay}
              </text>
              <text x={40} y={50} textAnchor="middle" fontSize={9} fill="#9aada8" fontFamily="inherit">
                of {totalScheduleDays}
              </text>
            </svg>
          </div>

          {/* Stats column */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Groups done */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#707070' }}>Groups done</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#1F4C40' }}>{groupsDone} / {totalGroups}</span>
              </div>
              <div style={{ height: 5, backgroundColor: '#E0E0E0', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${totalGroups > 0 ? (groupsDone / totalGroups) * 100 : 0}%`,
                  backgroundColor: '#1F4C40',
                  borderRadius: 999,
                  transition: 'width 400ms ease',
                }} />
              </div>
            </div>
            {/* Days done */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#707070' }}>Days done</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#1F4C40' }}>{daysDone} / {totalScheduleDays}</span>
              </div>
              <div style={{ height: 5, backgroundColor: '#E0E0E0', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${totalScheduleDays > 0 ? (daysDone / totalScheduleDays) * 100 : 0}%`,
                  backgroundColor: '#1F4C40',
                  borderRadius: 999,
                  transition: 'width 400ms ease',
                }} />
              </div>
            </div>
          </div>
        </div>

        {/* Time strip */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div style={{
            backgroundColor: '#fff',
            border: '1px solid #E0E0E0',
            borderRadius: 12,
            padding: '10px 12px',
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1F4C40', lineHeight: 1, marginBottom: 4 }}>
              {formatTime(elapsedTime)}
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#707070' }}>
              Total time
            </div>
          </div>
          <div style={{
            backgroundColor: '#fff',
            border: '1px solid #E0E0E0',
            borderRadius: 12,
            padding: '10px 12px',
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1F4C40', lineHeight: 1, marginBottom: 4 }}>
              {formatTime(todayTime)}
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#707070' }}>
              Today
            </div>
          </div>
        </div>
      </div>

      {/* Today's schedule card */}
      {currentSchedule && (
        <div style={{
          backgroundColor: '#fff',
          borderRadius: 20,
          border: '1px solid #E0E0E0',
          padding: '20px',
          marginBottom: 14,
        }}>
          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <p className="eyebrow" style={{ margin: '0 0 4px' }}>Today</p>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1F4C40', letterSpacing: '-0.5px' }}>
                Day {currentDay} schedule
              </h2>
            </div>
            <span style={{
              backgroundColor: '#D4EDE6',
              color: '#1F4C40',
              borderRadius: 999,
              padding: '4px 10px',
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
              marginLeft: 8,
            }}>
              {todayWordCount} words
            </span>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#478475', flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: '#707070' }}>New</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#C0C0C0', flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: '#707070' }}>Review</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#7DB55A', flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: '#707070' }}>Recent</span>
            </div>
          </div>

          {/* Group chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {newGroups.map(g => (
              <span key={`new-${g}`} style={{
                backgroundColor: '#D4EDE6',
                color: '#1F4C40',
                border: '1px solid #9FE1CB',
                borderRadius: 999,
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 700,
              }}>
                Group {g}
              </span>
            ))}
            {reviewGroups.map(g => (
              <span key={`rev-${g}`} style={{
                backgroundColor: '#F7F7F7',
                color: '#707070',
                border: '1px solid #E0E0E0',
                borderRadius: 999,
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 400,
              }}>
                Group {g}
              </span>
            ))}
            {recentGroupsList.map(g => (
              <span key={`rec-${g}`} style={{
                backgroundColor: '#EAF3DE',
                color: '#3B6D11',
                border: '1px solid #C0DD97',
                borderRadius: 999,
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 400,
              }}>
                Group {g}
              </span>
            ))}
          </div>

          {/* Summary line */}
          <p style={{ fontSize: 13, color: '#707070', margin: '0 0 16px' }}>
            {currentSchedule.allGroups.length} group{currentSchedule.allGroups.length !== 1 ? 's' : ''} · {todayWordCount} words
            {reviewGroups.length === 0 ? ' · no review groups yet' : ''}
          </p>

          {/* Start studying button */}
          <button
            onClick={startDay}
            disabled={isDayCompleted}
            style={{
              width: '100%',
              padding: '15px',
              fontSize: 15,
              fontWeight: 700,
              cursor: isDayCompleted ? 'not-allowed' : 'pointer',
              backgroundColor: isDayCompleted ? '#478475' : '#1F4C40',
              color: '#fff',
              border: 'none',
              borderRadius: 999,
              marginBottom: 8,
              opacity: isDayCompleted ? 0.75 : 1,
            }}
          >
            {isDayCompleted ? '✓ Day completed' : 'Start studying'}
          </button>

          {/* Skip to next day button */}
          {currentDay < schedule.length && (
            <button
              onClick={skipToNextDay}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: 15,
                fontWeight: 400,
                cursor: 'pointer',
                backgroundColor: 'transparent',
                color: '#707070',
                border: '1.5px solid #E0E0E0',
                borderRadius: 999,
              }}
            >
              Skip to next day →
            </button>
          )}
        </div>
      )}

      {/* Full schedule preview — collapsible */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: 20,
        border: '1px solid #E0E0E0',
        overflow: 'hidden',
      }}>
        {/* Toggle header */}
        <button
          onClick={() => setScheduleExpanded(p => !p)}
          style={{
            width: '100%',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 700,
            color: '#222',
          }}
        >
          <span>Full schedule preview</span>
          <span style={{
            fontSize: 12,
            fontWeight: 400,
            color: '#478475',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}>
            <span style={{
              display: 'inline-block',
              transition: 'transform 0.2s ease',
              transform: scheduleExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}>▼</span>
            {scheduleExpanded ? 'hide' : 'show'}
          </span>
        </button>

        {scheduleExpanded && (
          <div>
            {visibleScheduleDays.map((daySchedule, idx) => {
              const isToday = daySchedule.day === currentDay;
              const isDone = !!progress?.dayCompleted[daySchedule.day];
              const dayNewGroups = daySchedule.allGroups.filter(g =>
                !daySchedule.rotatingGroups.includes(g) && !daySchedule.recentGroups.includes(g)
              );
              const dayWordCount = daySchedule.allGroups.reduce((sum, g) => sum + (groups[g - 1]?.length || 0), 0);
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const isLast = idx === visibleScheduleDays.length - 1 && !showAllScheduleDays && schedule.length > 5;

              return (
                <div key={daySchedule.day}>
                  {idx > 0 && (
                    <div style={{ height: 1, backgroundColor: '#F0F0F0', marginLeft: 20 }} />
                  )}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 20px',
                  }}>
                    {/* Day circle */}
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: isToday ? '#1F4C40' : '#F0F0F0',
                      color: isToday ? '#fff' : '#707070',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}>
                      {isDone ? '✓' : daySchedule.day}
                    </div>

                    {/* Schedule info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: '#707070' }}>
                        {dayNewGroups.length > 0
                          ? `Groups ${dayNewGroups.join(', ')} — new`
                          : `Groups ${daySchedule.allGroups.slice(0, 3).join(', ')}${daySchedule.allGroups.length > 3 ? '…' : ''}`
                        }
                        {daySchedule.recentGroups.length > 0 && ` · ${daySchedule.recentGroups.length} recent`}
                      </div>
                      <div style={{ fontSize: 11, color: '#9aada8', marginTop: 2 }}>
                        {dayWordCount} words
                      </div>
                    </div>

                    {/* Today badge */}
                    {isToday && (
                      <span style={{
                        backgroundColor: '#478475',
                        color: '#fff',
                        borderRadius: 999,
                        padding: '2px 8px',
                        fontSize: 10,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}>
                        Today
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Show more link */}
            {!showAllScheduleDays && schedule.length > 5 && (
              <div>
                <div style={{ height: 1, backgroundColor: '#F0F0F0', marginLeft: 20 }} />
                <button
                  onClick={() => setShowAllScheduleDays(true)}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 11,
                    color: '#9aada8',
                    textAlign: 'left',
                  }}
                >
                  + {schedule.length - 5} more days
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FastAttackGame;
